import { useState, useCallback, useRef } from 'react'

type UploadResult = {
  url: string
  filename?: string
}

type Props = {
  documentType: string
  label: string
  description: string
  required?: boolean
  initialUrl?: string
  provisionalReservationToken?: string
  uploadEndpoint?: string
  extraFormData?: Record<string, string>
  showRemoveButton?: boolean
  onUploadComplete: (result: UploadResult) => void
  onError: (error: string) => void
}

// initialUrlからファイルタイプを推測する
function detectFileType(url: string | undefined): 'pdf' | 'image' | null {
  if (!url) return null
  const lowerUrl = url.toLowerCase()
  if (lowerUrl.endsWith('.pdf')) return 'pdf'
  if (
    lowerUrl.endsWith('.jpg') ||
    lowerUrl.endsWith('.jpeg') ||
    lowerUrl.endsWith('.png') ||
    lowerUrl.endsWith('.gif') ||
    lowerUrl.endsWith('.heic') ||
    lowerUrl.endsWith('.heif')
  ) {
    return 'image'
  }
  return 'image' // デフォルトは画像として扱う
}

export default function DocumentUpload({
  documentType,
  label,
  description,
  required = false,
  initialUrl,
  provisionalReservationToken,
  uploadEndpoint = '/online_clinic/reservations/upload_document',
  extraFormData,
  showRemoveButton = true,
  onUploadComplete,
  onError,
}: Props) {
  const [isUploading, setIsUploading] = useState(false)
  // initialUrlがPDFの場合は'pdf'を、それ以外はURLをそのままpreviewにセット
  const initialPreview = initialUrl
    ? detectFileType(initialUrl) === 'pdf'
      ? 'pdf'
      : initialUrl
    : null
  const [preview, setPreview] = useState<string | null>(initialPreview)
  const [filename, setFilename] = useState<string | null>(null)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(initialUrl || null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      const resetSelection = () => {
        setPreview(null)
        setFilename(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }

      // ファイルサイズチェック（10MB以下）
      if (file.size > 10 * 1024 * 1024) {
        const actualMb = (file.size / 1024 / 1024).toFixed(1)
        onError(`ファイルサイズが大きすぎます（${actualMb}MB）。10MB以下の画像をアップロードしてください`)
        resetSelection()
        return
      }

      // プレビュー表示（画像の場合）
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          setPreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      } else if (file.type === 'application/pdf') {
        // PDFの場合はアイコンを表示
        setPreview('pdf')
      }

      setFilename(file.name)
      setIsUploading(true)

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('document_type', documentType)
        if (provisionalReservationToken) {
          formData.append('provisional_reservation_token', provisionalReservationToken)
        }
        if (extraFormData) {
          for (const [key, value] of Object.entries(extraFormData)) {
            formData.append(key, value)
          }
        }

        const csrfToken =
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
          },
          body: formData,
        })

        if (!response.ok) {
          let errorMessage = `アップロードに失敗しました（サーバーエラー: ${response.status}）`
          try {
            const errorData = await response.json()
            if (errorData?.error) errorMessage = errorData.error
          } catch {
            // JSONでないレスポンス（プロキシの502/504など）はそのまま既定メッセージを使う
          }
          // eslint-disable-next-line no-console
          console.error('[DocumentUpload] upload failed', {
            status: response.status,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          })
          onError(errorMessage)
          resetSelection()
          return
        }

        const data = await response.json()

        if (data.success) {
          setUploadedUrl(data.url)
          onUploadComplete({ url: data.url, filename: data.filename })
        } else {
          onError(data.error || 'アップロードに失敗しました')
          resetSelection()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[DocumentUpload] upload exception', error, {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        })
        const message =
          error instanceof TypeError
            ? 'ネットワークエラーが発生しました。通信状態をご確認の上、再度お試しください'
            : `アップロードに失敗しました（${error instanceof Error ? error.message : '不明なエラー'}）`
        onError(message)
        resetSelection()
      } finally {
        setIsUploading(false)
      }
    },
    [documentType, provisionalReservationToken, uploadEndpoint, extraFormData, onUploadComplete, onError]
  )

  const handleRemove = useCallback(() => {
    setPreview(null)
    setFilename(null)
    setUploadedUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onUploadComplete({ url: '' })
  }, [onUploadComplete])

  const handleClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return (
    <section className="border-b border-[#d4d6d7] py-6 last:border-b-0 last:pb-0">
      <div className="mb-3">
        <h3 className="oc-field-title">
          <span>■{label}</span>
          {required ? (
            <span className="oc-badge-required">必須</span>
          ) : (
            <span className="oc-badge-optional">任意</span>
          )}
        </h3>

        {/* 保険証セクション: 写真の例 */}
        {documentType === 'insurance_card' && (
          <>
            <div className="bg-[#F4FAFE] h-[20px] text-[#2196F3] text-center px-0 py-1 text-xs mt-2">写真の例</div>
            <img
              src="/assets/online_clinic/upload_sample.png"
              alt="写真の例"
              className="w-full h-[100px] inline-block object-contain"
            />
          </>
        )}

        <div className="text-sm text-[#6f7680] bg-[#F4FAFE] p-4 mt-2">
          {description}
          {documentType === 'insurance_card' && (
            <p className="text-xs text-[#2196F3] flex gap-1 items-center mt-2">
              <img src="/assets/online_clinic/question_icon.png" alt="" className="!h-[14px]" />
              <a
                href="https://anamne.wraptas.site/127f5cc5be6a803994e1fa8405f2a6da"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                医療保険資格の取得方法
              </a>
            </p>
          )}
          {documentType === 'recipient_certificate' && (
            <>
              <p className="mt-2">助成資格により適用条件が異なります。詳しくは自治体（市区町村）の担当窓口にお問い合わせください。</p>
              <p className="text-xs text-[#2196F3] flex gap-1 items-center mt-2">
                <img src="/assets/online_clinic/question_icon.png" alt="" className="!h-[14px]" />
                <a
                  href="https://anamne.wraptas.site/c45cc8a75b86430bbc8f398a82747295"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  助成資格の適用条件
                </a>
              </p>
            </>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {!uploadedUrl && !isUploading && (
        <>
          <button
            type="button"
            onClick={handleClick}
            className="button-secondary mt-3"
          >
            ファイルまたは写真をアップロード
          </button>
          <p className="mt-2 text-xs text-[#7f8388]">JPG, PNG, HEIC, PDF（10MB以下）</p>
        </>
      )}

      {isUploading && (
        <div className="mt-3 flex items-center justify-center rounded-lg bg-[#eef3f7] p-6">
          <svg
            className="animate-spin h-6 w-6 text-cyan-500 mr-2"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-gray-600">アップロード中...</span>
        </div>
      )}

      {uploadedUrl && !isUploading && (
        <div className="mt-3 relative">
          <div className="relative overflow-hidden rounded-lg border border-[#d8dde3] bg-[#f3f5f7]">
            {preview === 'pdf' ? (
              <div className="flex items-center justify-center p-6">
                <svg className="h-16 w-16 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 18a.5.5 0 01-.5-.5v-3a.5.5 0 011 0v3a.5.5 0 01-.5.5zm2.5-3.5c0-.28.22-.5.5-.5h1.5a.5.5 0 010 1H12v.5h1.5a.5.5 0 010 1H12v1a.5.5 0 01-1 0v-3zm4 2a1 1 0 011-1h.5a.5.5 0 010 1H16v1a.5.5 0 01-1 0v-1z" />
                </svg>
                <span className="ml-2 text-gray-700">{filename}</span>
              </div>
            ) : preview ? (
              <img
                src={preview}
                alt="プレビュー"
                className="w-full h-48 object-contain"
              />
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-green-600 flex items-center">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              アップロード完了
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClick}
                className="text-sm text-[#2196f3] hover:underline"
              >
                変更
              </button>
              {showRemoveButton && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="text-sm text-red-600 hover:underline"
                >
                  削除
                </button>
              )}
            </div>
          </div>
          {filename && <p className="text-xs text-gray-500 mt-1">{filename}</p>}
        </div>
      )}

      {/* 保険証セクション: 自費リスクの注意文 */}
      {documentType === 'insurance_card' && (
        <p className="text-xs text-gray-600 mt-2">
          保険資格の有効性を確認できない場合、保険診療の対象外（自費）となる可能性があります。
        </p>
      )}
    </section>
  )
}
